"use client";

import { useState, useRef, useEffect, useLayoutEffect, useMemo, type ReactNode } from "react";
import { code } from "@streamdown/code";
import {
  Streamdown,
  type Components as StreamdownComponents,
  type PluginConfig,
  type StreamdownProps,
} from "streamdown";
import { ExpandCollapseToggle } from "../composites/expand-collapse-toggle";
import { MentionChip, getMarkdownText, parseMentionHref } from "../inputs/mention-chip-editor";
import { usePublicIdHrefResolver } from "../lib/public-id-link-context";
import { cn } from "../utils";
import { JsonCodeBlock } from "./json-code-block";
import { linkifyPublicIds } from "./linkify-public-ids";
import { isAutolinkText, truncateUrlMiddle } from "./truncate-url";
import { WrappableCodeBlock } from "./wrappable-code-block";

const useIsomorphicLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;

// Fenced code blocks should work in every MarkdownRenderer surface. Callers
// can still supply additional Streamdown plugins (or a custom code plugin).
const defaultPlugins = {
  code,
} satisfies PluginConfig;

const interactiveCodeRenderers = [
  { language: "json", component: JsonCodeBlock },
  {
    language: ["javascript", "js", "typescript", "ts", "tsx", "jsx", "ruby", "rb"],
    component: WrappableCodeBlock,
  },
] satisfies NonNullable<PluginConfig["renderers"]>;

const themeClasses = {
  gray: {
    text: "text-foreground",
    heading: "text-foreground",
    inlineCode: "bg-muted text-foreground border border-border",
    blockquote: "border-border text-muted-foreground",
    link: "text-foreground underline underline-offset-4",
  },
  blue: {
    text: "text-primary",
    heading: "text-primary",
    inlineCode: "bg-primary/10 text-primary border border-primary/20",
    blockquote: "border-primary/30 text-primary",
    link: "text-primary underline underline-offset-4",
  },
  green: {
    text: "text-green-700 dark:text-green-400",
    heading: "text-green-800 dark:text-green-300",
    inlineCode:
      "bg-green-100 dark:bg-green-900/30 text-green-900 dark:text-green-100 border border-green-200 dark:border-green-800",
    blockquote: "border-green-400 dark:border-green-600 text-green-700 dark:text-green-400",
    link: "text-green-800 underline underline-offset-4 dark:text-green-300",
  },
};

/**
 * An autolink's children are a single text node holding the URL. An authored
 * `[label](url)` link can be a string too, so the caller still has to compare
 * the text against the href.
 */
function loneTextChild(children: ReactNode): string | null {
  if (typeof children === "string") return children;
  if (Array.isArray(children) && children.length === 1 && typeof children[0] === "string") {
    return children[0];
  }
  return null;
}

interface MarkdownRendererProps {
  content: string;
  theme?: "gray" | "blue" | "green";
  className?: string;
  /** When set, clamps the rendered output to this many lines with an expand toggle. */
  maxLines?: number;
  density?: "default" | "compact";
  mode?: StreamdownProps["mode"];
  plugins?: PluginConfig;
  controls?: StreamdownProps["controls"];
  isAnimating?: boolean;
  animated?: StreamdownProps["animated"];
  lineNumbers?: boolean;
  dir?: StreamdownProps["dir"];
  shikiTheme?: StreamdownProps["shikiTheme"];
  mermaid?: StreamdownProps["mermaid"];
  caret?: StreamdownProps["caret"];
}

export function MarkdownRenderer({
  content,
  theme = "gray",
  className = "",
  maxLines,
  density = "default",
  mode = "static",
  plugins,
  controls = false,
  isAnimating = false,
  animated,
  lineNumbers = false,
  dir,
  shikiTheme,
  mermaid,
  caret,
}: MarkdownRendererProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isClamped, setIsClamped] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const markdown = useMemo(() => getMarkdownText(content), [content]);

  useIsomorphicLayoutEffect(() => {
    if (!maxLines || !contentRef.current || isExpanded) return;
    const el = contentRef.current;
    setIsClamped(el.scrollHeight > el.clientHeight);
  }, [markdown, maxLines, isExpanded]);

  const classes = themeClasses[theme];
  const isCompact = density === "compact";
  const publicIdResolver = usePublicIdHrefResolver();
  const rendererPlugins = useMemo(
    () => ({
      ...defaultPlugins,
      ...plugins,
      // Preserve Streamdown's native copy/download/line-number UI in viewers
      // that enable it. Task descriptions use the interactive source controls.
      ...(controls || lineNumbers
        ? plugins?.renderers
          ? { renderers: plugins.renderers }
          : {}
        : { renderers: [...(plugins?.renderers ?? []), ...interactiveCodeRenderers] }),
    }),
    [controls, lineNumbers, plugins],
  );
  const components = useMemo<StreamdownComponents>(
    () => ({
      ul: ({ className: nodeClassName, ...props }) => (
        <ul
          className={cn(
            "list-disc list-outside ml-4",
            isCompact ? "my-0.5 space-y-0.5" : "my-2 space-y-1",
            classes.text,
            nodeClassName,
          )}
          {...props}
        />
      ),
      ol: ({ className: nodeClassName, ...props }) => (
        <ol
          className={cn(
            "list-decimal list-outside ml-4",
            isCompact ? "my-0.5 space-y-0.5" : "my-2 space-y-1",
            classes.text,
            nodeClassName,
          )}
          {...props}
        />
      ),
      li: ({ className: nodeClassName, ...props }) => (
        <li
          className={cn(
            "pl-1 marker:text-foreground",
            isCompact ? "[&>p]:contents [&>ul]:mt-1 [&>ol]:mt-1" : "",
            nodeClassName,
          )}
          {...props}
        >
          {linkifyPublicIds(props.children, publicIdResolver, classes.link)}
        </li>
      ),
      p: ({ className: nodeClassName, ...props }) => (
        <p
          className={cn(isCompact ? "my-0 [&+p]:mt-1" : "my-2", classes.text, nodeClassName)}
          {...props}
        >
          {linkifyPublicIds(props.children, publicIdResolver, classes.link)}
        </p>
      ),
      h1: ({ className: nodeClassName, ...props }) => (
        <h1
          className={cn(
            "text-lg font-bold",
            isCompact ? "mt-2.5 mb-1" : "mt-4 mb-2",
            classes.heading,
            nodeClassName,
          )}
          {...props}
        >
          {linkifyPublicIds(props.children, publicIdResolver, classes.link)}
        </h1>
      ),
      h2: ({ className: nodeClassName, ...props }) => (
        <h2
          className={cn(
            "text-base font-bold",
            isCompact ? "mt-2 mb-1" : "mt-3 mb-2",
            classes.heading,
            nodeClassName,
          )}
          {...props}
        >
          {linkifyPublicIds(props.children, publicIdResolver, classes.link)}
        </h2>
      ),
      h3: ({ className: nodeClassName, ...props }) => (
        <h3
          className={cn(
            "text-sm font-bold",
            isCompact ? "mt-1.5 mb-0.5" : "mt-2 mb-1",
            classes.heading,
            nodeClassName,
          )}
          {...props}
        >
          {linkifyPublicIds(props.children, publicIdResolver, classes.link)}
        </h3>
      ),
      inlineCode: ({ className: nodeClassName, ...props }) => (
        <code
          className={cn(classes.inlineCode, "rounded px-1 py-0.5 text-xs", nodeClassName)}
          {...props}
        />
      ),
      table: ({ className: nodeClassName, ...props }) => (
        <div className={cn(isCompact ? "my-1.5" : "my-3", "overflow-x-auto")}>
          <table
            className={cn("w-full border-collapse text-left text-sm", classes.text, nodeClassName)}
            {...props}
          />
        </div>
      ),
      thead: ({ className: nodeClassName, ...props }) => (
        <thead className={cn("border-b border-border", nodeClassName)} {...props} />
      ),
      tbody: ({ className: nodeClassName, ...props }) => (
        <tbody className={cn(nodeClassName)} {...props} />
      ),
      tr: ({ className: nodeClassName, ...props }) => (
        <tr className={cn("border-b border-border/60 last:border-b-0", nodeClassName)} {...props} />
      ),
      th: ({ className: nodeClassName, ...props }) => (
        <th
          className={cn(
            "px-3 py-2 align-top text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground",
            nodeClassName,
          )}
          {...props}
        >
          {linkifyPublicIds(props.children, publicIdResolver, classes.link)}
        </th>
      ),
      td: ({ className: nodeClassName, ...props }) => (
        <td className={cn("px-3 py-2 align-top", classes.text, nodeClassName)} {...props}>
          {linkifyPublicIds(props.children, publicIdResolver, classes.link)}
        </td>
      ),
      hr: ({ className: nodeClassName, ...props }) => (
        <hr
          className={cn(
            "border-0 border-t border-border",
            isCompact ? "my-2" : "my-4",
            nodeClassName,
          )}
          {...props}
        />
      ),
      blockquote: ({ className: nodeClassName, ...props }) => (
        <blockquote
          className={cn(
            "border-l-4 pl-4 italic",
            isCompact ? "my-1.5" : "my-4",
            classes.blockquote,
            nodeClassName,
          )}
          {...props}
        >
          {linkifyPublicIds(props.children, publicIdResolver, classes.link)}
        </blockquote>
      ),
      a: ({ href, children: linkChildren, className: nodeClassName, ...props }) => {
        const mention = href ? parseMentionHref(href) : null;
        if (mention) {
          return (
            <MentionChip
              id={mention.id}
              type={mention.type}
              className="!whitespace-normal [overflow-wrap:anywhere]"
            >
              {linkChildren}
            </MentionChip>
          );
        }
        // A bare URL autolinks with the whole URL as its text. Shorten the
        // display — keeping the href intact — so a 200-character SharePoint
        // link stays readable and cannot widen its container.
        const text = loneTextChild(linkChildren);
        const autolinked = href && text !== null && isAutolinkText(text, href) ? text : null;
        return (
          <a
            href={href}
            rel="noopener noreferrer nofollow"
            title={autolinked ? href : undefined}
            className={cn(classes.link, "[overflow-wrap:anywhere]", nodeClassName)}
            {...props}
          >
            {autolinked ? truncateUrlMiddle(autolinked) : linkChildren}
          </a>
        );
      },
    }),
    [
      classes.blockquote,
      classes.heading,
      classes.inlineCode,
      classes.link,
      classes.text,
      isCompact,
      publicIdResolver,
    ],
  );

  const clampStyle =
    maxLines && !isExpanded
      ? {
          overflow: "hidden" as const,
          display: "-webkit-box" as const,
          WebkitLineClamp: maxLines,
          WebkitBoxOrient: "vertical" as const,
        }
      : undefined;

  return (
    <div>
      <div ref={contentRef} style={clampStyle}>
        <Streamdown
          mode={mode}
          controls={controls}
          isAnimating={isAnimating}
          lineNumbers={lineNumbers}
          className={cn(
            "size-full [&>*:first-child]:mt-0 [&>*:last-child]:mb-0",
            // Prose must never widen its container. `anywhere` (unlike
            // `break-word`) also shrinks min-content width, which is what
            // actually stops a long token from pushing a flex/grid parent out.
            // Code blocks scroll instead, so opt them back out.
            "[overflow-wrap:anywhere] [&_pre]:[overflow-wrap:normal]",
            // streamdown@2.5.0 omits `block` on highlighted line spans when
            // lineNumbers is false, so fenced rows concatenate into one line.
            "[&_[data-streamdown=code-block-body]_code>span]:block",
            className,
          )}
          components={components}
          plugins={rendererPlugins}
          {...(animated !== undefined ? { animated } : {})}
          {...(dir !== undefined ? { dir } : {})}
          {...(shikiTheme !== undefined ? { shikiTheme } : {})}
          {...(mermaid !== undefined ? { mermaid } : {})}
          {...(caret !== undefined ? { caret } : {})}
        >
          {markdown}
        </Streamdown>
      </div>
      {maxLines && (isClamped || isExpanded) && (
        <ExpandCollapseToggle isExpanded={isExpanded} onToggle={() => setIsExpanded(!isExpanded)} />
      )}
    </div>
  );
}
